import React, { useEffect, useState, useMemo } from "react";
import SubscriptionManager from "../../services/dashboard/SubscriptionManager";
import { useNavigate } from "react-router-dom";
import { UserRound } from "lucide-react";

const ExpiredSubscriptions = () => {
  const [expiredUsers, setExpiredUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();

  const packageTypeOrder = { ONE_BOOK: 1, TWO_BOOKS: 2, THREE_BOOKS: 3 };
  const planNameOrder = { ONE_MONTH: 1, THREE_MONTHS: 3, SIX_MONTHS: 6, ONE_YEAR: 12 };

  useEffect(() => {
    const fetchExpiredUsers = async () => {
      setLoading(true);
      try {
        const data = await SubscriptionManager.getAllExpiredSubscribedUsersWithDetails();
        // const filtered = (data || []).filter(
        //   (user) =>
        //     user.status === "EXPIRED" || (user.remainingDays != null && user.remainingDays <= 0)
        // );
        setExpiredUsers(data);
        setDisplayedUsers(data);
      } catch (error) {
        console.error("Failed to fetch expired subscriptions:", error);
        setExpiredUsers([]);
        setDisplayedUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpiredUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setDisplayedUsers(expiredUsers);
      return;
    }

    const lower = searchTerm.trim().toLowerCase();
    const filtered = expiredUsers.filter((u) => {
      return (
        u.userName?.toLowerCase().includes(lower) ||
        u.emailId?.toLowerCase().includes(lower) ||
        u.planName?.toLowerCase().includes(lower)
      );
    });

    setDisplayedUsers(filtered);
  }, [searchTerm, expiredUsers]);

  const parseDateArray = (dateInput) => {
    // Handle ISO string format
    if (typeof dateInput === 'string') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }
    
    // Handle array format
    if (!Array.isArray(dateInput) || dateInput.length < 6) return null;
    const [y, m, d, h, min, s] = dateInput;
    return new Date(y, m - 1, d, h, min, s);
  };

  const formatDateArray = (dateInput) => {
    const d = parseDateArray(dateInput);
    return d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";
  };

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedUsers = useMemo(() => {
    const sortable = [...displayedUsers];
    const { key, direction } = sortConfig;
    if (!key) return sortable;

    sortable.sort((a, b) => {
      if (key === "planName") {
        const aVal = planNameOrder[a.planName] || 0;
        const bVal = planNameOrder[b.planName] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (key === "packageType") {
        const aVal = packageTypeOrder[a.packageType] || 0;
        const bVal = packageTypeOrder[b.packageType] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      let aVal = a[key];
      let bVal = b[key];

      if (key === "endDate") {
        const aDate = parseDateArray(aVal);
        const bDate = parseDateArray(bVal);
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [displayedUsers, sortConfig]);

  const handleViewUserDetails = (userId) => {
    navigate(`/admin/users/${userId}`);
  };
  useEffect(() => {
  setSortConfig({ key: "endDate", direction: "desc" });
}, []);


  return (
    <div className="min-h-screen bg-primary-shade1 p-6 font-nunito">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-text-highlight">Expired Subscriptions</h1>
        <input
          type="text"
          placeholder="Search by name, email, or plan..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-shade2"
        />
      </div>

      {loading ? (
        <div className="text-center text-lg text-text-muted">Loading...</div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-xl rounded-2xl">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-primary text-white">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">User ID</th>
                <th className="px-6 py-3 text-left font-semibold">Name</th>
                <th onClick={() => handleSort("planName")} className="px-6 py-3 text-left font-semibold cursor-pointer select-none">
                  Plan <span>{sortConfig.key === "planName" ? (sortConfig.direction === "asc" ? "▲" : "▼") : "▲▼"}</span>
                </th>
                <th onClick={() => handleSort("packageType")} className="px-6 py-3 text-left font-semibold cursor-pointer select-none">
                  Package <span>{sortConfig.key === "packageType" ? (sortConfig.direction === "asc" ? "▲" : "▼") : "▲▼"}</span>
                </th>
                <th onClick={() => handleSort("endDate")} className="px-6 py-3 text-left font-semibold cursor-pointer select-none">
                  End Date <span>{sortConfig.key === "endDate" ? (sortConfig.direction === "asc" ? "▲" : "▼") : "▲▼"}</span>
                </th>
                <th className="px-6 py-3 text-left font-semibold">Status</th>
                <th className="px-6 py-3 text-center font-semibold">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedUsers.map((user) => (
                <tr key={user.userId} className="hover:bg-primary-shade1 transition duration-150 ease-in-out">
                  <td className="px-6 py-4">{user.userId}</td>
                  <td className="px-6 py-4 font-medium text-gray-800">{user.userName}</td>
                  <td className="px-6 py-4">
                    <span className="bg-primary-shade1 text-primary-shade5 px-2 py-1 rounded-full text-xs font-semibold">{user.planName}</span>
                  </td>
                  <td className="px-6 py-4">{user.packageType}</td>
                  <td className="px-6 py-4">{formatDateArray(user.endDate)}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      {user.status || "EXPIRED"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button onClick={() => handleViewUserDetails(user.userId)} className="text-primary hover:text-primary-shade5 transition">
                      <UserRound className="inline-block text-lg" />
                    </button>
                  </td>
                </tr>
              ))}

              {sortedUsers.length === 0 && !loading && (
                <tr>
                  <td colSpan="7" className="text-center py-6 text-gray-500">
                    No expired subscriptions match “{searchTerm}.”
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExpiredSubscriptions;
